// iter118 Session 4 — Modal "Démarrer l'OR" depuis une session de prise en charge complète.
// - Affiche la carte grise photo (à gauche) + les champs détectés par OCR (à droite) pour validation
// - Bouton "Lancer l'OCR" si pas encore fait
// - L'admin peut éditer chaque champ avant création
// - Bouton "Créer l'OR" → POST /api/prise-en-charge/{token}/create-or → redirige vers /atelier/or/{id}
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ScanText, CheckCircle2, X, ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function MiniInput({ label, value, onChange, suggested, testid }) {
  const same = (value || "") === (suggested || "");
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        {suggested && !same && (
          <button
            type="button"
            onClick={() => onChange(suggested)}
            className="text-[10px] text-[#52a675] hover:underline"
          >
            ⟲ OCR : {String(suggested).slice(0, 30)}
          </button>
        )}
      </div>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:border-[#52a675] focus:ring-1 focus:ring-[#52a675]/40 focus:outline-none"
      />
    </label>
  );
}

export default function DemarrerOrModal({ open, token, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Champs OCR (suggested) vs champs édités (final)
  const [ocr, setOcr] = useState({});
  const [client, setClient] = useState({});
  const [vehicule, setVehicule] = useState({});
  const [remarques, setRemarques] = useState("");

  useEffect(() => {
    if (!open || !token) return undefined;
    let alive = true;
    axios.get(`${API}/public/prise-en-charge/${token}`)
      .then(({ data }) => {
        if (!alive) return;
        setSession(data);
        setClient(data.client_draft || {});
        setVehicule(data.vehicule_draft || {});
        setRemarques(data.remarques || "");
        if (data.ocr_carte_grise) setOcr(data.ocr_carte_grise);
        setLoading(false);
      })
      .catch((e) => { if (alive) { toast.error(e.response?.data?.detail || "Erreur chargement session"); setLoading(false); } });
    return () => { alive = false; };
  }, [open, token]);

  const runOcr = async () => {
    setOcrLoading(true);
    try {
      const { data } = await axios.post(`${API}/prise-en-charge/${token}/ocr-carte-grise`, {}, { headers: authHeaders() });
      setOcr(data.fields || {});
      // Auto-apply : si un champ véhicule est vide, on remplit avec l'OCR
      setVehicule((v) => ({
        ...v,
        marque: v.marque || data.fields?.marque || "",
        modele: v.modele || data.fields?.modele || "",
        plaque: v.plaque || data.fields?.plaque || "",
        vin: v.vin || data.fields?.vin || "",
        premiere_mec: v.premiere_mec || data.fields?.premiere_mec || "",
      }));
      toast.success(`OCR terminé (confiance ${Math.round((data.fields?.confiance || 0) * 100)}%)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  const createOr = async () => {
    setCreating(true);
    try {
      const body = {
        client,
        vehicule: { ...vehicule, type_constructeur: ocr.type_constructeur, couleur: ocr.couleur, carburant: ocr.carburant },
        remarques,
      };
      const { data } = await axios.post(`${API}/prise-en-charge/${token}/create-or`, body, { headers: authHeaders() });
      toast.success(`OR ${data.or_numero} créé`);
      onClose && onClose();
      navigate(`/atelier/or/${data.or_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création OR");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  const cgUrl = session?.carte_grise_photo ? `${BACKEND}/${session.carte_grise_photo.startsWith("uploads/") ? session.carte_grise_photo : `uploads/${session.carte_grise_photo}`}` : null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="demarrer-or-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">🚀 Démarrer l&apos;OR</h2>
            <p className="text-xs text-gray-500">Valide les informations détectées puis crée l&apos;ordre de réparation.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="demarrer-or-close"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#52a675]" /></div>
        ) : (
          <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Carte grise photo + OCR */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Carte grise</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 min-h-[280px] flex items-center justify-center">
                {cgUrl ? (
                  <img src={cgUrl} alt="Carte grise" className="max-h-[400px] object-contain rounded" data-testid="cg-photo-preview" />
                ) : (
                  <div className="text-center text-gray-400"><ImageOff className="w-12 h-12 mx-auto mb-2" /><div className="text-sm">Aucune photo de carte grise</div></div>
                )}
              </div>
              <button
                onClick={runOcr}
                disabled={ocrLoading || !cgUrl}
                className="w-full px-4 py-2.5 bg-[#52a675] hover:bg-[#3d8a5c] text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="run-ocr-btn"
              >
                {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanText className="w-4 h-4" />}
                {Object.keys(ocr).length ? "Relancer l'OCR" : "Lancer l'OCR"}
              </button>
              {Object.keys(ocr).length > 0 && (
                <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-2">
                  ✓ OCR effectué. Confiance : <b>{Math.round((ocr.confiance || 0) * 100)}%</b>. Les champs marqués &quot;⟲ OCR&quot; à droite peuvent être appliqués d&apos;un clic.
                </div>
              )}
            </div>

            {/* Validation client + véhicule */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Véhicule</h3>
                <div className="grid grid-cols-2 gap-2">
                  <MiniInput label="Marque" value={vehicule.marque} onChange={(v) => setVehicule({ ...vehicule, marque: v })} suggested={ocr.marque} testid="dor-marque" />
                  <MiniInput label="Modèle" value={vehicule.modele} onChange={(v) => setVehicule({ ...vehicule, modele: v })} suggested={ocr.modele} testid="dor-modele" />
                  <MiniInput label="Plaque" value={vehicule.plaque} onChange={(v) => setVehicule({ ...vehicule, plaque: v.toUpperCase() })} suggested={ocr.plaque} testid="dor-plaque" />
                  <MiniInput label="VIN" value={vehicule.vin} onChange={(v) => setVehicule({ ...vehicule, vin: v.toUpperCase() })} suggested={ocr.vin} testid="dor-vin" />
                  <MiniInput label="1ère mise en circ." value={vehicule.premiere_mec} onChange={(v) => setVehicule({ ...vehicule, premiere_mec: v })} suggested={ocr.premiere_mec} testid="dor-mec" />
                  <MiniInput label="Kilométrage" value={vehicule.km} onChange={(v) => setVehicule({ ...vehicule, km: String(v).replace(/\D/g, "") })} testid="dor-km" />
                </div>
                {(ocr.couleur || ocr.carburant || ocr.type_constructeur) && (
                  <div className="text-[10px] text-gray-500 mt-1.5 bg-gray-50 rounded px-2 py-1 space-x-2">
                    {ocr.type_constructeur && <span>Type: <b>{ocr.type_constructeur}</b></span>}
                    {ocr.couleur && <span>· Couleur: <b>{ocr.couleur}</b></span>}
                    {ocr.carburant && <span>· Carburant: <b>{ocr.carburant}</b></span>}
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  Client
                  {client.type === "entreprise" && (
                    <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded" data-testid="dor-client-entreprise-badge">Entreprise</span>
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {client.type === "entreprise" ? (
                    <div className="col-span-2">
                      <MiniInput label="Raison sociale" value={client.raison_sociale} onChange={(v) => setClient({ ...client, raison_sociale: v })} testid="dor-client-raison-sociale" />
                    </div>
                  ) : (
                    <>
                      <MiniInput label="Nom" value={client.nom} onChange={(v) => setClient({ ...client, nom: v })} testid="dor-client-nom" />
                      <MiniInput label="Prénom" value={client.prenom} onChange={(v) => setClient({ ...client, prenom: v })} testid="dor-client-prenom" />
                    </>
                  )}
                  <MiniInput label="Téléphone" value={client.tel_principal} onChange={(v) => setClient({ ...client, tel_principal: v })} testid="dor-client-tel" />
                  <MiniInput label="Email" value={client.email} onChange={(v) => setClient({ ...client, email: v })} testid="dor-client-email" />
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Remarques client</h3>
                <textarea
                  value={remarques}
                  onChange={(e) => setRemarques(e.target.value)}
                  rows={3}
                  data-testid="dor-remarques"
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:border-[#52a675] focus:ring-1 focus:ring-[#52a675]/40 focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Annuler</button>
          <button
            onClick={createOr}
            disabled={creating || loading}
            className="px-5 py-2.5 bg-[#52a675] hover:bg-[#3d8a5c] text-white rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2"
            data-testid="dor-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Créer l&apos;OR
          </button>
        </div>
      </div>
    </div>
  );
}
